'use client'

import { useCallback, useEffect } from 'react'
import { Alert, AlertTitle, Box, Button, Stack } from '@mui/material'
import LaunchIcon from '@mui/icons-material/Launch'

import useWhatsappOfficialApi from '@/hooks/useWhatsappOfficialApi'
import {
  WHATSAPP_BILLING_STATUS,
  WHATSAPP_BILLING_STATUS_CHANGED_EVENT,
} from '@/models/whatsappTemplate'
import { COMPANY_STATUS, ROLES } from '@/shared/constants'
import { disconnectSocket, getSocket } from '@/socket'
import { useAppSelector } from '@/store/hooks'

const WHATSAPP_MANAGER_URL =
  'https://business.facebook.com/latest/whatsapp_manager'

export default function WhatsappBillingAlert() {
  const { user } = useAppSelector((state) => state.auth)
  const {
    billingStatus,
    getBillingStatus,
    confirmBillingConfigured,
    isBillingLoading,
  } = useWhatsappOfficialApi()
  const isCanceledCompany =
    user?.company?.status === COMPANY_STATUS.CANCELED
  const isOfficial =
    !isCanceledCompany &&
    user?.company?.whatsappIntegration?.transports?.some(
      (transport) => transport.type === 'official' && transport.isActive
    ) === true
  const isAdmin = user?.role?.id === ROLES.COMPANY_ADMIN
  const shouldShow =
    isOfficial &&
    (billingStatus?.status === WHATSAPP_BILLING_STATUS.UNVERIFIED ||
      billingStatus?.status === WHATSAPP_BILLING_STATUS.PAYMENT_PENDING)

  useEffect(() => {
    if (!isOfficial) return

    void getBillingStatus()
    const interval = window.setInterval(() => {
      void getBillingStatus(true)
    }, 60_000)

    return () => window.clearInterval(interval)
  }, [getBillingStatus, isOfficial])

  useEffect(() => {
    if (!isOfficial || !user?.companyId || !user.id) return

    const socket = getSocket(user.companyId, user.id)
    const handleBillingStatusChanged = () => {
      void getBillingStatus(true)
    }

    socket.on(WHATSAPP_BILLING_STATUS_CHANGED_EVENT, handleBillingStatusChanged)

    return () => {
      socket.off(
        WHATSAPP_BILLING_STATUS_CHANGED_EVENT,
        handleBillingStatusChanged
      )
      disconnectSocket()
    }
  }, [getBillingStatus, isOfficial, user?.companyId, user?.id])

  const openMetaBilling = useCallback(() => {
    window.open(
      billingStatus?.resolutionUrl ?? WHATSAPP_MANAGER_URL,
      '_blank',
      'noopener,noreferrer'
    )
  }, [billingStatus?.resolutionUrl])

  const handleConfirmation = useCallback(async () => {
    await confirmBillingConfigured()
  }, [confirmBillingConfigured])

  if (!shouldShow) return null

  return (
    <Alert
      severity="warning"
      sx={{
        borderRadius: 0,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        width: '100%',
        boxSizing: 'border-box',
        zIndex: 1299,
        py: 1,
        px: 2,
      }}
      action={
        isAdmin ? (
          <Box display="flex" alignItems="center" gap={1}>
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              endIcon={<LaunchIcon />}
              onClick={openMetaBilling}
            >
              Configurar na Meta
            </Button>
            <Button
              variant="contained"
              color="warning"
              size="small"
              disabled={isBillingLoading}
              onClick={handleConfirmation}
            >
              Já configurei
            </Button>
          </Box>
        ) : undefined
      }
    >
      <Stack spacing={0.5}>
        <AlertTitle mb={0.5}>Pagamento do WhatsApp pendente</AlertTitle>
        {isAdmin
          ? 'A API oficial está conectada, mas os dados de pagamento da Meta precisam ser configurados para enviar templates e iniciar campanhas.'
          : 'A API oficial está conectada, mas um administrador da empresa precisa concluir a configuração de pagamento na Meta.'}
      </Stack>
    </Alert>
  )
}
